import { format, isAfter, isToday, parseISO } from 'date-fns';

import Button from '@/components/shared/Button/Button';
import { Text } from '@/components/shared/Text/Text';
import { formatTimeWithTimeZone } from '@/lib/utils/format-time-with-timezone';
import { CoachSchedule } from '@addmile/backend-client';

interface CustomHoursProps {
  customHours: CoachSchedule['customHours'];
  timezone: string;
  onAddCustomHours: (customHours: Array<{ fromDateTime: string; toDateTime: string }>) => void;
}

const CustomHours = ({ customHours, timezone, onAddCustomHours }: CustomHoursProps) => {
  const today = new Date();

  const groupedCustomHours = customHours?.reduce(
    (acc: { [key: string]: Array<{ fromDateTime: string; toDateTime: string }> }, customHour) => {
      const date = customHour.fromDateTime.split('T')[0];
      const dateObj = parseISO(date);

      if (isToday(dateObj) || isAfter(dateObj, today)) {
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(customHour);
      }
      return acc;
    },
    {},
  );

  const deleteCustomHour = (hour: { fromDateTime: string; toDateTime: string }) => {
    const updatedCustomHours = customHours?.filter((ch) => ch.fromDateTime !== hour.fromDateTime);
    onAddCustomHours(updatedCustomHours || []);
  };

  return (
    <div className="w-full">
      <Text.H2 className="font-bold mb-8">Custom Hours</Text.H2>
      {Object.entries(groupedCustomHours || {}).map(([date, hours]) => (
        <div key={date} className="mb-4">
          <Text.H5 className="font-medium mb-4">{format(parseISO(date), 'E MMM dd, yyyy')}</Text.H5>
          {(hours as Array<{ fromDateTime: string; toDateTime: string }>).map((hour, index) => (
            <div key={`${hour.toDateTime}-${index}`} className="flex gap-2 items-center">
              <span className="font-semibold">
                {formatTimeWithTimeZone(hour.fromDateTime, timezone).time} -{' '}
                {formatTimeWithTimeZone(hour.toDateTime, timezone).time}
              </span>
              <Button
                isIcon
                variant="transparent"
                className="w-8 h-8 border-none"
                onClick={() => deleteCustomHour(hour)}
                prefixIcon={<img src={'/icons/close.svg'} />}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default CustomHours;
